import { Button, ButtonLink, DropdownMenu, ButtonLinkProps } from '@anytag/shared-atoms';
import { DropdownOption } from '@anytag/shared-types';
import { cn } from '@anytag/shared-utils';
import { getSpreadButtonIcon, SpreadButtonIcon } from './utils';

interface OptionItem
  extends Pick<DropdownOption<string>, 'label'>,
    Partial<Pick<ButtonLinkProps, 'tooltip' | 'disabled' | 'to' | 'params' | 'search'>> {
  hidden?: boolean;
  onClick?: () => void;
  icon?: keyof typeof SpreadButtonIcon;
  className?: string;
}

export interface SpreadButtonProps {
  className?: string;
  options: OptionItem[];
  collapseFrom?: number;
  moreButtonProps?: Pick<ButtonLinkProps, 'disabled'>;
}

export const SpreadButton = ({ options, className, moreButtonProps, collapseFrom }: SpreadButtonProps) => {
  // Example: collapseFrom = 2, options.length = 3 so mean visibleOptions.length = 2 and moreOptions.length = 1
  // When moreOptions.length = 1, it means we will show more button with 1 option only. In this case, we don't need to show the more button, show that option directly.
  const visibleOptions = options.filter((o) => !o.hidden);
  const moreOptions =
    typeof collapseFrom === 'number' && visibleOptions.length - 1 > collapseFrom
      ? visibleOptions.splice(collapseFrom)
      : [];

  return (
    <div className={cn('flex gap-1', className)}>
      {visibleOptions.length > 0
        ? visibleOptions.map(
            ({ to, params, search, icon, disabled, onClick, tooltip, className: buttonClassName }, index) =>
              to ? (
                <ButtonLink
                  key={`visible-button-${index}`}
                  to={to}
                  params={params}
                  search={search}
                  variant="outline"
                  disabled={disabled}
                  tooltip={tooltip}
                  prefixIcon={getSpreadButtonIcon(icon)}
                  className={cn(styles.button, 'single-button', buttonClassName)}
                />
              ) : (
                <Button
                  key={`visible-button-${index}`}
                  variant="outline"
                  onClick={onClick}
                  disabled={disabled}
                  tooltip={tooltip}
                  prefixIcon={getSpreadButtonIcon(icon)}
                  className={cn(styles.button, 'single-button', buttonClassName)}
                />
              )
          )
        : null}

      {moreOptions.length > 0 ? (
        <DropdownMenu
          trigger={
            <div css={{ pointerEvents: moreButtonProps?.disabled ? 'none' : 'initial' }}>
              <Button
                variant="outline"
                prefixIcon={getSpreadButtonIcon('more')}
                className={cn(styles.button, 'menu-button relative m-0')}
                {...moreButtonProps}
              />
            </div>
          }
          options={moreOptions.map(({ icon, to, params, search, label, tooltip: _, ...restOptionProps }, index) => ({
            value: index.toString(),
            disabled: restOptionProps.disabled,
            label: to ? (
              <ButtonLink
                to={to}
                params={params}
                search={search}
                title={label}
                variant="outline"
                className={styles.moreOptionItem(restOptionProps.disabled)}
                prefixIcon={getSpreadButtonIcon(icon)}
                {...restOptionProps}
              />
            ) : (
              <Button
                title={label}
                variant="outline"
                className={styles.moreOptionItem(restOptionProps.disabled)}
                prefixIcon={getSpreadButtonIcon(icon)}
                {...restOptionProps}
              />
            )
          }))}
        />
      ) : null}
    </div>
  );
};

const styles = {
  button: cn('min-w-unset size-6 p-0'),
  moreOptionItem: (disabled?: boolean) =>
    cn(
      'min-w-unset text-default h-auto w-full justify-start border-none! bg-transparent! p-0 leading-none font-normal text-primary',
      { 'opacity-50': disabled }
    )
};
